import buttonStyles from './Button/Button.module.less';
import ctaStyles from './CallToAction/CallToAction.module.less';
import { brandColors, brandLevels, brandSizes } from './constants';

/**
 * helpers specific to HVUI
 * from https://24ways.org/2010/calculating-color-contrast/
 * @param {string} hexColor the color you are contrasting.
 * @returns {string} Black or white, depending on which has sufficient color contrast based on the hex value passed in.
 */

export const getContrastYIQ = (
    hexColor,
    lightColor = '#fff',
    darkColor = '#000'
) => {
    // remove leading #
    if (hexColor.startsWith('#')) {
        hexColor = hexColor.slice(1);
    }
    const r = parseInt(hexColor.substr(0, 2), 16);
    const g = parseInt(hexColor.substr(2, 2), 16);
    const b = parseInt(hexColor.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? darkColor : lightColor;
};

/**
 * A helper for determining which classNames to use for what buttons and calls-to-action in various situations.
 * @param {string} prefix A string that indicates what sort of styles to make, e.g in the case of the className `button-primary`, `button` is the prefix.
 * @param {object} styles Imported styles from specific LESS files.
 * @returns {function} A function that will return styles.
 */
// TODO - this whole function could use a refactor
export const getClassNames =
    (prefix, styles) =>
    /**
     * For more information on valid options, see { brandLevels, brandColors, brandSizes } from './constants';
     * @param {object} props The React props of the given component.
     * @param {string} [props.className] A fully-formed className. Overrides the calculated style.
     * @param {string} [props.level] Hierarchy of the component. Can be `primary`, `secondary` for now. `tertiary` tbd.
     * @param {string} [props.color] Color of the component. Can be any of the brand colors.
     * @param {size} [props.size] Size of the component. Default size is indicated by null for this prop; can also be `narrow`.
     * @returns {string} The final string of classNames based on the above props.
     */
    props => {
        let { level, color, size } = props;
        let classNamesArray = [prefix];

        const isCWButton = () => {
            const isStorybook = process.env.STORYBOOK_ENV;

            const isJest = process.env.NODE_ENV === 'test';

            // If it's in the app, check the URL
            let path = window?.location?.pathname?.split('/')[1];

            // If it's Storybook, get the iframe's parent's story information in the query
            if (isStorybook) {
                path = window?.parent?.location?.search;
            }
            // apps that use a 'CW Button' from design system (not limited to just CW)
            const cwButtonRoutes = ['provider', 'registration'];
            if (path) {
                if (!isStorybook) {
                    return cwButtonRoutes.includes(path);
                } else {
                    return (
                        path.includes('clinic-workflow') ||
                        path.includes('patient-check-in')
                    );
                }
            }
            if (isJest && expect) {
                const { currentTestName } = expect.getState();
                return (
                    currentTestName.includes('CLINIC WORKFLOW') ||
                    currentTestName.includes('Patient Check In (dReg)')
                );
            }
            return false;
        };

        if (level) {
            if (!brandLevels.includes(level))
                throw new Error(
                    `'${level}' is not an existing style level, try again with: ${brandLevels.toString()}`
                );

            if (level === 'primary' && !color) {
                if (prefix === 'button') {
                    color = isCWButton() ? 'ocean-dk' : 'ocean';
                } else {
                    color = 'ocean';
                }
            }
            // TO DO: Add additional secondary colors as we define them
            if (level === 'secondary' && !color) {
                if (prefix === 'button') {
                    color = isCWButton() ? 'ocean-dk' : 'navy';
                } else {
                    color = 'navy';
                }
            }

            if (level === 'link-button') {
                classNamesArray = [level];
            }

            if (color && !brandColors.includes(color))
                throw new Error(
                    `'${color}' is not a prestyled color in the LESS file, try again with: ${brandColors.toString()}`
                );

            if (!level && !color) {
                color = 'ocean';
            }

            if (level === 'primary' || level === 'secondary') {
                classNamesArray.push(
                    [prefix, level, color]
                        .filter(attr => attr != null)
                        .join('-')
                );
            }
        }
        if (size) {
            if (!brandSizes.includes(size))
                throw new Error(
                    `'${size}' is not a prestyled size in the LESS file, try again with: ${brandSizes.toString()} or remove the prop to inherit the default.`
                );
            classNamesArray.push(`${prefix}-${size}`);
        }
        return classNamesArray.map(c => styles[c]).join(' ');
    };

/**
 * Returns appropriate classNames for HVUI buttons
 * @func
 */
export const getButtonClassNames = getClassNames('button', buttonStyles);
/**
 * Returns appropriate classNames for HVUI calls-to-action
 * @func
 */
export const getCTAClassNames = getClassNames('cta', ctaStyles);

/**
 *
 * @param {object} style A React style object
 * @returns {object} The new style object with contrast correctly calculated
 */
export const getInlineStylesWithContrast = style => {
    if (!style) return {};
    // Make a copy
    const newStyles = JSON.parse(JSON.stringify(style));
    // double check no one is trying to get fancy with colors
    const background = newStyles.background || newStyles.backgroundColor;
    if (background) {
        if (newStyles.color) {
            // don't trust your users
            console.warn(
                'text color is automatically computed for Button for a11y - you should not be manually specifying this'
            );
        }
        // this is fine; set a color for them
        const color = getContrastYIQ(background);
        newStyles.color = color;
    }

    if (!background && newStyles.color) {
        // _really_ don't trust your users
        // we could try to guess based on class name but this should be a
        // full error
        // force to unstyled as penance
        const color = getContrastYIQ('var(--gray400)'); // @gray400
        newStyles.color = color;
        console.error(
            'text color is automatically computed for Button for a11y - you should not be manually specifying this'
        );
    }
    return newStyles;
};
